import { useState, useCallback, useRef } from "react";

const BASE = "http://localhost:8535/api/signature";

export type SignaturePadStatus =
  | "idle"
  | "checking"
  | "waiting"    // pad active, user is signing
  | "captured"
  | "unavailable"
  | "error";

interface StartResult {
  available: boolean;
  success: boolean;
  error?: string;
}

interface PreviewResult {
  success: boolean;
  signatureDataUrl: string | null;
}

interface CaptureResult {
  success: boolean;
  signatureDataUrl?: string;
  error?: string;
}

const useSignaturePad = () => {
  const [status, setStatus] = useState<SignaturePadStatus>("idle");
  const abortRef = useRef<AbortController | null>(null);

  /** Activate the physical pad for a new capture session. */
  const startCapture = useCallback(async (): Promise<StartResult> => {
    setStatus("checking");
    try {
      const res = await fetch(`${BASE}/start`, {
        method: "POST",
        signal: AbortSignal.timeout(5_000),
      });
      const json: StartResult = await res.json().catch(() => ({ available: false, success: false }));
      setStatus(json.success ? "waiting" : "unavailable");
      return json;
    } catch {
      setStatus("unavailable");
      return { available: false, success: false, error: "Tray app unreachable" };
    }
  }, []);

  /**
   * Poll the pad for the current in-progress signature image (non-blocking).
   * Call this on a ~500 ms interval while status === "waiting" for a live preview.
   */
  const pollPreview = useCallback(async (): Promise<PreviewResult> => {
    try {
      const res = await fetch(`${BASE}/preview`, {
        signal: AbortSignal.timeout(3_000),
      });
      return await res.json().catch(() => ({ success: false, signatureDataUrl: null }));
    } catch {
      return { success: false, signatureDataUrl: null };
    }
  }, []);

  /**
   * Finalise: grab the current image from the pad, stop the session.
   * Call this when the user clicks Done in the web UI.
   */
  const captureSignature = useCallback(async (): Promise<CaptureResult> => {
    abortRef.current?.abort();
    try {
      const res = await fetch(`${BASE}/capture`, {
        method: "POST",
        signal: AbortSignal.timeout(5_000),
      });
      const json: CaptureResult = await res.json().catch(() => ({ success: false, error: "Invalid response" }));
      setStatus(json.success ? "captured" : "error");
      return json;
    } catch (e: any) {
      setStatus("error");
      return { success: false, error: e?.message ?? "Network error" };
    }
  }, []);

  /** Cancel any in-progress capture session on both client and server. */
  const cancelCapture = useCallback(async () => {
    abortRef.current?.abort();
    setStatus("idle");
    try {
      await fetch(`${BASE}/cancel`, {
        method: "POST",
        signal: AbortSignal.timeout(3_000),
      });
    } catch {
      // best-effort
    }
  }, []);

  const reset = useCallback(() => setStatus("idle"), []);

  return {
    status,
    isPadActive:    status === "waiting",
    isPadAvailable: status !== "unavailable",
    startCapture,
    pollPreview,
    captureSignature,
    cancelCapture,
    reset,
  };
};

export default useSignaturePad;
